import React, { useEffect, useState } from 'react';
import api from '../services/api';

// CHART BUY / SELL (owner 2026-10-16: "only Buy and Sell on the chart; the AI chooses CE/PE + strike").
// BUY on a stock / index chart: the AI's current read picks CE (bullish) or PE (bearish) - the at-the-money contract
// with its live premium, lot and cost is shown before you confirm (no direction -> you pick CE or PE).
// SELL: lists YOUR open positions on this symbol to close (no option writing).
const box = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' };

export function ChartBuyBox({ symbol, onClose, onResult }) {
  const [pv, setPv] = useState(null);
  const [err, setErr] = useState('');
  const [pick, setPick] = useState(0);
  const [lots, setLots] = useState(1);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    api.getAiOrderPreview(symbol).then(r => {
      if (!alive) return;
      if (r && r.status === 'success') setPv(r); else setErr((r && r.message) || 'No preview');
    }).catch(() => alive && setErr('Server not reachable'));
    return () => { alive = false; };
  }, [symbol]);
  const c = pv && pv.choices[pick];
  const send = async () => {
    if (!c) return;
    setBusy(true);
    try {
      const r = await api.myManualOrder(c.contract, c.exchange || 'NSE', 'BUY', Math.max(1, lots), c.lot_size || 1);
      onResult(r && r.status === 'success' ? 'success' : 'error', (r && r.message) || 'Order failed');
    } catch (e) { onResult('error', 'Order failed — server not reachable'); }
    setBusy(false); onClose();
  };
  const sigCol = pv && pv.ai.signal === 'BUY' ? '#00ff88' : pv && pv.ai.signal === 'SELL' ? '#ff6b6b' : '#ffb020';
  return (
    <div style={box} onClick={onClose}>
      <div className="card" style={{ maxWidth: '380px', width: '100%', border: '1px solid #00c46a' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 700, color: '#00ff88', marginBottom: '0.4rem' }}>BUY on {symbol}</div>
        {!pv && !err && <div style={{ color: '#888' }}>AI is choosing CE / PE and the strike…</div>}
        {err && <div style={{ color: '#ff6b6b' }}>{err}</div>}
        {pv && (
          <>
            <div style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>
              <b style={{ color: sigCol }}>AI: {pv.ai.signal}{pv.ai.score != null ? ` (score ${pv.ai.score})` : ''}</b>
              <span style={{ color: '#aaa' }}> — {(pv.ai.reasons || []).slice(0, 2).join(' · ') || pv.ai.action}</span>
              {pv.choices.length > 1 && <div style={{ color: '#ffb020', marginTop: 2 }}>AI has no clear direction now — choose CE or PE:</div>}
            </div>
            {pv.choices.map((x, k) => (
              <label key={x.contract} style={{ display: 'block', fontSize: '0.85rem', margin: '0.2rem 0', cursor: 'pointer' }}>
                {pv.choices.length > 1 && <input type="radio" checked={pick === k} onChange={() => setPick(k)} />}{' '}
                <b style={{ color: x.opt_type === 'CE' ? '#00ff88' : '#ff6b6b' }}>{x.contract}</b>
                <span style={{ color: '#ccc' }}> · premium ₹{x.premium} · lot {x.lot_size} · 1 lot ₹{(+x.cost_1lot).toLocaleString('en-IN')}</span>
              </label>
            ))}
            <label style={{ display: 'block', fontSize: '0.85rem', color: '#ccc', margin: '0.5rem 0' }}>
              Lots: <input type="number" min="1" value={lots} onChange={e => setLots(Math.max(1, parseInt(e.target.value, 10) || 1))} style={{ width: '70px' }} />
              {c && <span style={{ color: '#888' }}> = {lots * c.lot_size} units ≈ ₹{Math.round(lots * c.cost_1lot).toLocaleString('en-IN')}</span>}
            </label>
            <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.6rem' }}>Goes to YOUR account in your current mode (Paper or Live). The bot then manages the stop and exit.</div>
          </>
        )}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-start" disabled={!c || busy} onClick={send}>{busy ? 'Sending…' : 'Confirm'}</button>
          <button className="btn" disabled={busy} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function ChartSellBox({ held, onClose, onResult }) {
  const [busy, setBusy] = useState('');
  const close = async (ts) => {
    setBusy(ts);
    try {
      const r = await api.myManualOrder(ts, 'NSE', 'SELL', 1, 1);
      onResult(r && r.status === 'success' ? 'success' : 'error', (r && r.message) || 'Close failed');
    } catch (e) { onResult('error', 'Close failed — server not reachable'); }
    setBusy(''); onClose();
  };
  return (
    <div style={box} onClick={onClose}>
      <div className="card" style={{ maxWidth: '380px', width: '100%', border: '1px solid #ff4444' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 700, color: '#ff6b6b', marginBottom: '0.5rem' }}>SELL (close) your position</div>
        {held.map(v => {
          const ts = String(v.trade_symbol || v.symbol || '').toUpperCase().split(':').pop();
          const ep = +v.entry_price || 0; const cp = +v.current_price || 0;
          const pct = ep ? ((cp - ep) / ep) * 100 : 0;
          return (
            <div key={v.key || ts} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.3rem 0', fontSize: '0.85rem' }}>
              <b style={{ flex: 1 }}>{ts}</b>
              <span style={{ color: pct >= 0 ? '#00ff88' : '#ff6b6b' }}>{pct >= 0 ? '+' : ''}{pct.toFixed(1)}%</span>
              <button className="btn btn-stop" disabled={!!busy} onClick={() => close(ts)}>{busy === ts ? '…' : 'Close'}</button>
            </div>
          );
        })}
        <button className="btn" style={{ marginTop: '0.5rem' }} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
